/**
 * Password reset screen - Palantir PMS
 * Author: Alberto Castro Ull
 * Date: 07/08/2025
 * All rights reserved.
 */

import React, { useEffect, useState } from "react";
import pilcoreLogo from "../assets/Logo_Pilcore_Vertical_Blanco.svg";
import axios from "../api/axiosInstance";
import "../LoginScreen.css";

const ResetPasswordScreen = () => {
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [tokenUsable, setTokenUsable] = useState(false);
  const bothPasswordsEntered = password.length > 0 && repeatPassword.length > 0;
  const passwordsMismatch = bothPasswordsEntered && password !== repeatPassword;

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");

  useEffect(() => {
    let cancelled = false;

    const checkToken = async () => {
      setError(null);
      setSuccessMsg(null);
      setTokenExpired(false);
      setTokenUsable(false);

      if (!token) {
        setError("The link is not valid or the token is missing.");
        setCheckingToken(false);
        return;
      }

      setCheckingToken(true);

      try {
        const { data } = await axios.post(
          "/api/users/reset-password-token-status",
          { token },
          { skipCrossTabSync: true }
        );

        if (cancelled) return;

        if (data?.status === "expired_link_resent") {
          setTokenExpired(true);
          setSuccessMsg(
            data.message ||
              "This password link has expired. We sent a new link to your email."
          );
          return;
        }

        setTokenUsable(true);
      } catch (err) {
        if (cancelled) return;

        if (err?.response?.data?.error) {
          setError(err.response.data.error);
        } else {
          setError("Error checking the link. Try again or request a new link.");
        }
      } finally {
        if (!cancelled) {
          setCheckingToken(false);
        }
      }
    };

    checkToken();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!token) {
      setError("The link is not valid or the token is missing.");
      return;
    }

    if (checkingToken) {
      setError("Checking the link. Please wait a moment.");
      return;
    }

    if (tokenExpired) {
      setSuccessMsg("This password link has expired. We sent a new link to your email.");
      return;
    }

    if (!tokenUsable) {
      setError("The link is not valid or has already been used.");
      return;
    }

    if (password.length < 12) {
      setError("Password must be at least 12 characters long.");
      return;
    }

    if (password !== repeatPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(
        "/api/users/reset-password",
        {
          token,
          password,
        },
        { skipCrossTabSync: true }
      );

      setSuccessMsg("Password has been set. You can log in.");
      setPassword("");
      setRepeatPassword("");

      setTimeout(() => {
        window.location.assign("/");
      }, 2500);

    } catch (err) {
      if (err?.response?.data?.code === "PASSWORD_RESET_TOKEN_EXPIRED_LINK_RESENT") {
        setTokenExpired(true);
        setTokenUsable(false);
        setPassword("");
        setRepeatPassword("");
        setSuccessMsg(err.response.data.error);
      } else if (err?.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Error saving the password. Try again or request a new link.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <img src={pilcoreLogo} alt="Pilcore watermark" className="auth-watermark" />

      <div className="auth-shell">
        <div className="auth-heading">
          <p className="auth-subtitle">Create your new password securely.</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-card">
          <h5 className="auth-cardTitle">
            New Password
          </h5>

          <div className="auth-group">
            <label className="auth-label">New Password</label>
            <input
              type="password"
              className="auth-input"
              value={password}
              minLength={12}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              autoComplete="new-password"
              disabled={submitting || checkingToken || !tokenUsable}
            />
            <div className="auth-help">Minimum 12 characters.</div>
          </div>

          <div className="auth-group">
            <label className="auth-label">Confirm password</label>
            <input
              type="password"
              className="auth-input"
              value={repeatPassword}
              onChange={(e) => setRepeatPassword(e.target.value)}
              required
              autoComplete="new-password"
              disabled={submitting || checkingToken || !tokenUsable}
            />
          </div>

          {passwordsMismatch && (
            <div className="auth-error" role="alert">Passwords do not match.</div>
          )}
          {error && <div className="auth-error">{error}</div>}
          {successMsg && <div className="auth-success">{successMsg}</div>}

          {bothPasswordsEntered && (
            <button
              type="submit"
              className="auth-btn-primary"
              disabled={submitting || checkingToken || !tokenUsable || passwordsMismatch}
            >
              {checkingToken ? "Checking..." : submitting ? "Saving..." : "Set password"}
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordScreen;
